import React from 'react';
import { TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const GraficoReceita = ({ dadosReceita = [] }) => {
  // Paleta de cores profissional
  const COR_PRIMARIA = '#4F46E5'; // Índigo vibrante
  const COR_SECUNDARIA = '#10B981'; // Verde esmeralda para receita_instalacao

  // Função para formatar o mês
  const formatarMes = (mesString) => {
    if (!mesString) return '';
    
    // Se já estiver no formato abreviado, retorna direto
    if (typeof mesString === 'string' && mesString.length === 3) {
      return mesString;
    }
    
    // Tenta extrair o mês de diferentes formatos
    let mesNumero = '';
    if (typeof mesString === 'string') {
      // Formato YYYY-MM
      if (mesString.includes('-')) {
        mesNumero = mesString.split('-')[1];
      } 
      // Formato MM (ex: '01')
      else if (mesString.length === 2) {
        mesNumero = mesString;
      }
    }
    
    const meses = {
      '01': 'Jan', '02': 'Fev', '03': 'Mar', '04': 'Abr',
      '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Ago',
      '09': 'Set', '10': 'Out', '11': 'Nov', '12': 'Dez'
    };
    
    return meses[mesNumero] || mesString;
  };
  
  // Processar dados com tratamento mais robusto
  const dadosProcessados = Array.isArray(dadosReceita)
    ? dadosReceita
        .filter(item => item && typeof item === 'object')
        .map(item => {
          const mes = item?.mes ? formatarMes(item.mes) : '';
          // Tentar diferentes campos para receita
          const receita = typeof item?.receita === 'number' ? item.receita : 
                         typeof item?.receita_total === 'number' ? item.receita_total : 
                         typeof item?.valor === 'number' ? item.valor : 0;
          
          // Adicionar receita_instalacao - converter para número se necessário
          const receitaInstalacao = typeof item?.receita_instalacao === 'number' 
            ? item.receita_instalacao 
            : (item?.receita_instalacao != null && !isNaN(parseFloat(item.receita_instalacao)))
              ? parseFloat(item.receita_instalacao)
              : 0;
          
          return {
            ...item,
            mes,
            receita,
            receita_instalacao: receitaInstalacao
          };
        })
    : [];

  // Tooltip customizado para mostrar ambas as receitas
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[color:var(--surface)] p-3 rounded-md shadow-md border border-[color:var(--border)]">
          <p className="font-medium text-[color:var(--text-primary)] mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p 
              key={index}
              className="font-semibold"
              style={{ color: entry.color }}
            >
              {entry.name}: R$ {entry.value?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-[color:var(--text-primary)] flex items-center whitespace-nowrap">
          <TrendingUp className="h-5 w-5 text-[color:var(--accent)] mr-2 flex-shrink-0" />
          <span className="truncate">Evolução da Receita de Vendas</span>
        </h3>
      </div>
      
      <div className="h-64">
        {dadosProcessados.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={dadosProcessados}
              margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
              <XAxis 
                dataKey="mes"
                tick={{ fill: '#4b5563', fontSize: 12, fontWeight: 500 }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
              />
              <YAxis
                tick={{ fill: '#4b5563', fontSize: 12 }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
                tickFormatter={(value) => `R$ ${(value/1000).toFixed(0)}k`}
                domain={['auto', 'auto']}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                verticalAlign="bottom"
                height={40}
                iconType="line"
                iconSize={10}
                formatter={(value) => {
                  if (value === 'receita') return 'Receita de Vendas';
                  if (value === 'receita_instalacao') return 'Receita de Instalação';
                  return value;
                }}
              />
              {/* Linha de Receita de Vendas */}
              <Line
                type="monotone"
                dataKey="receita"
                stroke={COR_PRIMARIA}
                strokeWidth={2}
                dot={{ 
                  fill: COR_PRIMARIA, 
                  stroke: '#fff', 
                  strokeWidth: 2, 
                  r: 5,
                  filter: 'drop-shadow(0px 2px 4px rgba(79, 70, 229, 0.3))'
                }}
                activeDot={{ 
                  r: 7, 
                  stroke: '#fff', 
                  strokeWidth: 2, 
                  fill: COR_PRIMARIA,
                  filter: 'drop-shadow(0px 2px 6px rgba(79, 70, 229, 0.5))'
                }}
                name="Receita de Vendas"
              />
              {/* Linha de Receita de Instalação */}
              <Line
                type="monotone"
                dataKey="receita_instalacao"
                stroke={COR_SECUNDARIA}
                strokeWidth={2}
                dot={{ 
                  fill: COR_SECUNDARIA, 
                  stroke: '#fff', 
                  strokeWidth: 2, 
                  r: 5,
                  filter: 'drop-shadow(0px 2px 4px rgba(16, 185, 129, 0.3))'
                }}
                activeDot={{ 
                  r: 7, 
                  stroke: '#fff', 
                  strokeWidth: 2, 
                  fill: COR_SECUNDARIA,
                  filter: 'drop-shadow(0px 2px 6px rgba(16, 185, 129, 0.5))'
                }}
                name="Receita de Instalação"
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-[color:var(--text-muted)] p-4 text-center">
            <div className="text-lg mb-2">📊</div>
            <p>Nenhum dado disponível para exibição</p>
            <p className="text-sm mt-1 text-[color:var(--text-muted)]">
              {dadosReceita.length > 0 
                ? 'Os dados não puderam ser processados' 
                : 'Adicione dados para visualizar o gráfico'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GraficoReceita;




